/*global chrome*/

import React from 'react';

// Helpers
import { testGetUserMedia } from '../../helpers/helpers';

import { config } from '../../config';

// Components
import Webcam from '../UserMedia/Webcam';
import Microphone from '../UserMedia/Microphone';
import * as icons from '../Icons/Icons';

const SystemCheck = class SystemCheck extends React.Component {
  constructor() {
    super();

    // Bind this to functions
    this.complete = this.complete.bind(this);
    this.checkOptions = this.checkOptions.bind(this);
    this.isCameraBlank = this.isCameraBlank.bind(this);
    this.startFaceCheck = this.startFaceCheck.bind(this);

    // Set the initial state
    this.state = {
      audioDetected: false,
      ready: false,
      mediaChecked: false,
      getUserMedia: false,
      devicesChecked: false,
      optionsChecked: false,
      webcams: [],
      microphones: [],
      averageBrightness: 0,
      unacceptableBrightness: false
    };
  }

  async componentDidUpdate(prevProps, prevState) {
    const { nextStep, parent, setDevice, statusClass, button } = this.props;
    const translation = this.props.translation.system_check;

    if (statusClass === 'slide-active' && parent.state.next !== nextStep) {
      if (this.props.runFaceCheck) {
        // Wait for Face Check
        button.disabled = true;
        button.textContent = translation.waiting_for_face_detection;
      }

      // Check if getUserMedia is supported
      if (!this.state.mediaChecked) {
        const getUserMediaSupported = await testGetUserMedia();
        this.getUserMediaSupported = getUserMediaSupported;

        if (!getUserMediaSupported) {
          this.setState({
            getUserMedia: false
          });

          this.disableNext();
        } else {
          this.setState({
            getUserMedia: true
          });
        }

        this.setState({
          mediaChecked: true
        });
      }
    }

    // Get user devices
    if (
      this.state.mediaChecked &&
      this.state.getUserMedia &&
      !this.state.devicesChecked
    ) {
      navigator.mediaDevices
        .enumerateDevices()
        .then(devices => {
          const webcams = [];
          const microphones = [];

          devices.forEach(device => {
            if (device.kind === 'videoinput') {
              webcams.push(device);
            } else if (device.kind === 'audioinput') {
              microphones.push(device);
            }
          });

          // Set the webcam to the first one
          setDevice('webcam', `${webcams[0].deviceId},${webcams[0].label}`);

          // Set the microphone to the first one
          setDevice('microphone', `${microphones[0].deviceId},${microphones[0].label}`);

          this.setState({
            webcams,
            microphones
          });

          this.startFaceCheck();
        })
        .catch(error => {
          console.error(error);
        });

      this.setState({
        devicesChecked: true
      });
    }

    if (statusClass === 'slide-active' && this.state.webcams.length > 0) {
      this.startFaceCheck();
    }

    // If getUserMedia has been checked and is available, set ready to true
    if (
      statusClass === 'slide-active' &&
      this.state.mediaChecked &&
      this.state.getUserMedia &&
      !this.state.ready
    ) {
      this.setState(
        {
          ready: true
        },
        this.checkOptions
      );
    }

    // Remove the webcam preview after proceeding
    if (statusClass !== 'slide-active' && this.state.ready) {
      this.setState({
        ready: false
      });
    }

    if (
      (statusClass === 'slide-active' && !this.state.optionsChecked) ||
      (prevProps.displayCount !== this.props.displayCount &&
        statusClass === 'slide-active')
    ) {
      if (this.state.getUserMedia) {
        this.disableNext();
        this.checkOptions();
      }
    }
  }

  startFaceCheck() {
    const { button, faceCheck, runFaceCheck, store } = this.props;
    const { displayCountValid } = this.state;
    const { SessionStore } = store;
    const translation = this.props.translation.system_check;
    let checkCount = 0;

    clearInterval(this.faceCheckInterval);

    // If face check is disabled, enable the button,
    // update the text, and return.
    if (!runFaceCheck && displayCountValid && this.getUserMediaSupported) {
      button.disabled = false;
      button.textContent = translation.button;
      return;
    }

    const faceCheckInterval = setInterval(
      () =>
        faceCheck(faceDetected => {
          checkCount += 1;

          console.log(`Face detected: ${faceDetected}`);

          if (checkCount === 6) {
            SessionStore.showToaster(
              translation.face_not_found,
              'error',
              100000,
              'toaster--lg'
            );
          }

          if (faceDetected) {
            clearInterval(this.faceCheckInterval);
            SessionStore.closeNotification();

            if (displayCountValid) {
              button.disabled = false;
              button.textContent = translation.button;
            }
          }
        }),
      1000
    );
    this.faceCheckInterval = faceCheckInterval;
  }

  getAverageBrightness(imgData) {
    let d = imgData.data;
    let rgb = { r: 0, g: 0, b: 0 };
    for (let i = 0; i < d.length; i += 4) {
      rgb.r += d[i];
      rgb.g += d[i + 1];
      rgb.b += d[i + 2];
    }

    rgb.r = ~~(rgb.r / (d.length / 4));
    rgb.g = ~~(rgb.g / (d.length / 4));
    rgb.b = ~~(rgb.b / (d.length / 4));

    return (
      // https://en.wikipedia.org/wiki/Relative_luminance
      Math.round(((0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255) * 100 * 100) /
      100
    );
  }

  isCameraBlank() {
    try {
      const video = document.getElementsByClassName('webcam__stream').item(0);
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
      const context = canvas.getContext('2d');
      const averageBrightness = this.getAverageBrightness(
        context.getImageData(0, 0, 1, 1)
      );
      this.setState({ averageBrightness });
      return averageBrightness < config.averageBrightnessMin;
    } catch (_) {
      return true;
    }
  }

  checkOptions() {
    const { button, displayCount, examConfiguration, parent } = this.props;
    const { modalityOptions } = examConfiguration;

    this.setState({
      optionsChecked: true,
      displayCountValid:
        !modalityOptions.onlyOneScreen ||
        (modalityOptions.onlyOneScreen && displayCount === 1)
          ? true
          : false
    });

    const isScreenCountNotAllowed =
      typeof modalityOptions.onlyOneScreen !== 'undefined' && displayCount > 1;

    let isScreenBrightnessUnacceptable =
      modalityOptions.recordWebcam !== 'undefined' &&
      this.state.ready &&
      this.isCameraBlank();

    if (isScreenBrightnessUnacceptable !== this.state.unacceptableBrightness) {
      this.setState({
        unacceptableBrightness: isScreenBrightnessUnacceptable
      });
    }

    if (!modalityOptions.recordWebcam) {
      isScreenBrightnessUnacceptable = false;

      this.setState({
        unacceptableBrightness: false
      });
    }

    if (
      ((isScreenCountNotAllowed || isScreenBrightnessUnacceptable) &&
        (parent.state.next !== null || !button.disabled)) ||
      !this.state.audioDetected
    ) {
      this.disableNext();
    } else if (parent.state.next === null || button.disabled) {
      this.enableNext();
    }

    if (modalityOptions.recordWebcam && this.state.ready) {
      if (this.state.webcams.length === 0) {
        clearTimeout(this.webcamRecheck);
        this.webcamRecheck = setTimeout(this.checkOptions, 500);
        this.disableNext();
      }
    }

    if (modalityOptions.recordAudio && this.state.ready) {
      if (this.state.microphones.length === 0) {
        clearTimeout(this.micRecheck);
        this.micRecheck = setTimeout(this.checkOptions, 500);
        this.disableNext();
      }
    }
  }

  disableNext() {
    const { button } = this.props;

    button.disabled = true;
  }

  enableNext() {
    const { button, parent } = this.props;

    button.disabled = false;

    parent.setState({
      next: this.complete
    });
  }

  complete() {
    const { nextStep } = this.props;

    this.setState({
      optionsChecked: false
    });

    nextStep();
  }

  render() {
    const { displayCount, examConfiguration, setDevice, statusClass } = this.props;
    const { microphones, unacceptableBrightness, webcams } = this.state;
    const { modalityOptions } = examConfiguration;
    const translation = this.props.translation.system_check;

    return (
      <main
        className={`panel panel--medium system-check ${statusClass}`}
        aria-labelledby="step8"
      >
        <h2 className="panel__title panel__title--alt" id="step8">
          {translation.heading}
        </h2>

        {this.state.ready &&
          modalityOptions.recordWebcam &&
          statusClass === 'slide-active' && (
            <Webcam
              {...this.props}
              name="preview"
              camera="user"
              parent={this}
              disableFullScreen={true}
              hideTools={true}
              caption={translation.body}
            />
          )}

        {unacceptableBrightness && (
          <span className="note note--prominent note--alt system-check__note">
            {translation.unacceptable_brightness}
          </span>
        )}

        {/* Webcam and microphone required, but not present */}
        {modalityOptions.recordWebcam &&
          modalityOptions.recordAudio &&
          (webcams.length === 0 || microphones.length === 0) && (
            <span className="note note--prominent note--alt system-check__note">
              {translation.no_devices}
            </span>
          )}

        {/* Webcam required, but not present */}
        {modalityOptions.recordWebcam &&
          !modalityOptions.recordAudio &&
          webcams.length === 0 && (
            <span className="note note--prominent note--alt system-check__note">
              {translation.no_webcam}
            </span>
          )}

        {/* Microphone required, but not present */}
        {!modalityOptions.recordWebcam &&
          modalityOptions.recordAudio &&
          microphones.length === 0 && (
            <span className="note note--prominent note--alt system-check__note">
              {translation.no_microphone}
            </span>
          )}

        <ul className="grid system-check__grid">
          {modalityOptions.recordWebcam && (
            <li className="grid__cell media--alt media--alt-top">
              <icons.iconVideoCamera />

              <div className="media__copy">
                <label htmlFor="check-webcam">{translation.webcam}</label>

                <label className="select">
                  <select
                    id="check-webcam"
                    onChange={e => {
                      this.startFaceCheck();
                      setDevice('webcam', e.target.value);
                    }}
                  >
                    {this.state.webcams.map((webcam, i) => (
                      <option value={`${webcam.deviceId},${webcam.label}`} key={i}>
                        {webcam.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </li>
          )}

          {modalityOptions.recordAudio && (
            <li className="grid__cell media--alt media--alt-top">
              {this.state.ready && (
                <Microphone
                  {...this.props}
                  onAudioDetected={() => {
                    this.setState({ audioDetected: true }, () => {
                      this.checkOptions();
                    });
                  }}
                />
              )}

              <div className="media__copy">
                <label htmlFor="check-microphone">{translation.microphone}</label>

                <label className="select">
                  <select
                    id="check-microphone"
                    onChange={e => {
                      setDevice('microphone', e.target.value);

                      this.setState({ audioDetected: false }, () => {
                        this.props.button.disabled = true;
                      });
                    }}
                  >
                    {this.state.microphones.map((microphone, i) => (
                      <option
                        value={`${microphone.deviceId},${microphone.label}`}
                        key={i}
                      >
                        {microphone.label}
                      </option>
                    ))}
                  </select>
                </label>

                {!this.state.audioDetected && (
                  <span className="media__note">{translation.microphone_helper}</span>
                )}
              </div>
            </li>
          )}

          <li className="grid__cell media--alt">
            <icons.iconWifi />

            <div className="media__copy">
              <strong className="media__heading">{translation.connection}</strong>

              <span className="tally__status tally__status--alt completed">
                <span className="meta">Pass</span>
              </span>
            </div>
          </li>

          {modalityOptions.onlyOneScreen && (
            <li className="grid__cell media--alt">
              <icons.iconDesktop />

              <div className="media__copy">
                <strong className="media__heading">
                  {translation.displays.heading}: {displayCount}
                </strong>

                {displayCount === 1 && (
                  <span className="tally__status tally__status--alt completed">
                    <span className="meta">Pass</span>
                  </span>
                )}

                {displayCount !== 1 && <span>{translation.displays.error_msg}</span>}
              </div>
            </li>
          )}
        </ul>
      </main>
    );
  }
};

export default SystemCheck;



// WEBPACK FOOTER //
// ./src/app/components/SessionStart/SystemCheck.js